import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { authService } from '../services/authService';
import socketService from '../services/socketService';
import { signInWithPhoneNumber } from 'firebase/auth';
import { auth, getRecaptchaVerifier } from '../firebaseClient';

// Validate critical imports to prevent minification issues
if (!authService || typeof authService !== 'object') {
  console.error('CRITICAL: Auth service not properly loaded in AuthProvider');
}
if (!socketService || typeof socketService !== 'object') {
  console.error('CRITICAL: Socket service not properly loaded in AuthProvider');
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState('user'); // 'user' or 'admin'
  const [loading, setLoading] = useState(true);
  const otpTimerRef = useRef(null);

  // OTP related state
  const [otpState, setOtpState] = useState({
    phoneNumber: '',
    email: '',
    otpSent: false,
    otpVerified: false,
    countdown: 0,
    canResend: false,
    type: 'phone', // 'phone' or 'email'
    firebaseConfirmation: null,
  });

  useEffect(() => {
    // Initialize auth state quickly for UX, then verify in background.
    const init = async () => {
      console.log('🔵 [AuthProvider] Initializing authentication state...');

      const savedUserStr = localStorage.getItem('mgx_user');
      const savedUserType = localStorage.getItem('mgx_user_type');
      // Read token from multiple fallbacks to be resilient across flows
      const token =
        localStorage.getItem('auth_token') ||
        localStorage.getItem('mgx_token') ||
        sessionStorage.getItem('auth_token') ||
        sessionStorage.getItem('mgx_token');

      const refreshToken = localStorage.getItem('refresh_token');

      console.log('🔍 [AuthProvider] Session check:', {
        hasUser: !!savedUserStr,
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
        userType: savedUserType
      });

      if (savedUserStr && token) {
        try {
          const parsedUser = JSON.parse(savedUserStr);
          console.log('✅ [AuthProvider] Restoring session for:', {
            userId: parsedUser._id || parsedUser.id,
            email: parsedUser.email,
            role: parsedUser.role
          });

          // Optimistically restore session immediately
          setUser(parsedUser);
          setUserType(savedUserType || 'user');
          // Initialize socket with token
          socketService.updateAuthToken(token);
          if (!socketService.isConnected()) {
            socketService.init();
          }
        } catch (e) {
          // Corrupt data, clear
          console.error('❌ [AuthProvider] Corrupt session data, clearing:', e);
          localStorage.removeItem('mgx_user');
          localStorage.removeItem('mgx_user_type');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('mgx_token');
        }

        // Background verification: don't force logout on a single 401 during bootstrap.
        // The axios layer will already attempt a refresh if possible.
        authService.getProfile()
          .then((res) => {
            // Normalize profile shape
            const profileUser = res?.user || res?.data?.user || res;
            if (profileUser && typeof profileUser === 'object') {
              const normalizedUser = profileUser;
              const derivedType = normalizedUser.role || savedUserType || 'user';
              console.log('✅ [AuthProvider] Profile verified successfully');
              setUser(normalizedUser);
              setUserType(derivedType);
              // Persist to localStorage to survive future refreshes
              try {
                localStorage.setItem('mgx_user', JSON.stringify(normalizedUser));
              } catch { }
              localStorage.setItem('mgx_user_type', derivedType);
            }
          })
          .catch((err) => {
            console.warn('⚠️ [AuthProvider] Profile verification failed:', err.status);
            // Only clear session if we definitively have no way to recover (no refresh token)
            if (err?.status === 401 && !refreshToken) {
              console.log('🔴 [AuthProvider] No refresh token, clearing session');
              setUser(null);
              setUserType('user');
              localStorage.removeItem('mgx_user');
              localStorage.removeItem('mgx_user_type');
              localStorage.removeItem('auth_token');
              localStorage.removeItem('mgx_token');
              localStorage.removeItem('refresh_token');
              socketService.disconnect();
            } else if (refreshToken) {
              console.log('🔄 [AuthProvider] Will attempt token refresh on next request');
            }
          });
      } else {
        console.log('ℹ️ [AuthProvider] No saved session found');
      }

      setLoading(false);
      console.log('🔵 [AuthProvider] Initialization complete');
    };

    init();
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (otpTimerRef.current) {
        clearInterval(otpTimerRef.current);
      }
    };
  }, []);

  // Define login function before refreshToken to avoid temporal dead zone
  const login = (userData, type = 'user', token = null, refreshToken = null) => {
    console.log('🟢 [AuthProvider] Login called:', {
      userId: userData?._id || userData?.id,
      email: userData?.email,
      type,
      hasToken: !!token,
      hasRefreshToken: !!refreshToken
    });

    setUser(userData);
    setUserType(type);

    // Store user data
    try {
      localStorage.setItem('mgx_user', JSON.stringify(userData));
      localStorage.setItem('mgx_user_type', type);
      console.log('✅ [AuthProvider] User data stored in localStorage');
    } catch (error) {
      console.error('❌ [AuthProvider] Failed to store user data:', error);
    }

    // Store token if provided
    if (token) {
      try {
        localStorage.setItem('auth_token', token);
        localStorage.setItem('mgx_token', token);
        sessionStorage.setItem('auth_token', token);
        sessionStorage.setItem('mgx_token', token);
        console.log('✅ [AuthProvider] Auth token stored');

        // Initialize socket connection with new token
        socketService.updateAuthToken(token);
        if (!socketService.isConnected()) {
          socketService.init();
        }
      } catch (error) {
        console.error('❌ [AuthProvider] Failed to store auth token:', error);
      }
    } else {
      console.warn('⚠️ [AuthProvider] No auth token provided to login function');
    }

    // Store refresh token if provided
    if (refreshToken) {
      try {
        localStorage.setItem('refresh_token', refreshToken);
        console.log('✅ [AuthProvider] Refresh token stored');
      } catch (error) {
        console.error('❌ [AuthProvider] Failed to store refresh token:', error);
      }
    }

    // Verify storage worked
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('mgx_user');
    if (!storedToken || !storedUser) {
      console.error('❌ [AuthProvider] Storage verification failed!', {
        tokenStored: !!storedToken,
        userStored: !!storedUser
      });
    } else {
      console.log('✅ [AuthProvider] Storage verification passed');
    }
  };

  const logout = () => {
    setUser(null);
    setUserType('user');
    localStorage.removeItem('mgx_user');
    localStorage.removeItem('mgx_user_type');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    // Disconnect socket on logout
    socketService.disconnect();
    // Clear OTP timer
    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
      otpTimerRef.current = null;
    }
    // Reset OTP state on logout
    setOtpState({
      phoneNumber: '',
      email: '',
      otpSent: false,
      otpVerified: false,
      countdown: 0,
      canResend: false,
      type: 'phone',
      firebaseConfirmation: null,
    });
  };

  const verifyToken = async (token) => {
    try {
      // Verify by fetching profile; if it works, token is valid
      await authService.getProfile();
    } catch (error) {
      console.error('Token verification error:', error);
      // Only logout on explicit 401; for network/CORS/timeouts keep session and retry later
      if (error?.status === 401) {
        logout();
      }
    }
  };

  // Enhanced token refresh logic
  const refreshToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken();
      if (response.accessToken) {
        // Store new tokens
        localStorage.setItem('auth_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }

        // Update socket service with new token
        socketService.updateAuthToken(response.accessToken);

        // If we have user data, update it with new token
        if (user) {
          login(user, userType, response.accessToken, response.refreshToken);
        }

        return response.accessToken;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, logout user
      logout();
      throw error;
    }
  }, [user, userType]); // Removed login from dependencies as it's stable

  // OTP Functions
  const sendOTP = async (phoneNumber) => {
    // Validate phone number format (keep existing validation)
    const phoneRegex = /^\+91\s\d{10}$/;
    if (!phoneRegex.test(phoneNumber)) {
      throw new Error('Please enter a valid phone number in format: +91 9876543210');
    }

    // Clear any existing timer
    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
    }

    try {
      // Use Firebase Phone Auth instead of backend SMS provider
      const verifier = getRecaptchaVerifier('recaptcha-container');
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, verifier);

      // Update state on success
      setOtpState(prev => ({
        ...prev,
        phoneNumber,
        firebaseConfirmation: confirmation,
        otpSent: true,
        otpVerified: false,
        countdown: 60, // 60 seconds countdown
        canResend: false,
        type: 'phone',
      }));

      // Start countdown timer
      otpTimerRef.current = setInterval(() => {
        setOtpState(prev => {
          if (prev.countdown <= 1) {
            clearInterval(otpTimerRef.current);
            otpTimerRef.current = null;
            return { ...prev, countdown: 0, canResend: true };
          }
          return { ...prev, countdown: prev.countdown - 1 };
        });
      }, 1000);

      return { success: true, message: 'OTP sent via Firebase' };
    } catch (error) {
      console.error('Firebase send OTP error:', error);
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  };

  const verifyOTP = async (otp) => {
    if (!otpState.otpSent) {
      throw new Error('Please request OTP first');
    }

    try {
      // Phone-based OTP verification via Firebase
      if (otpState.type === 'phone') {
        if (!otpState.firebaseConfirmation) {
          throw new Error('OTP session expired. Please request a new code.');
        }

        // Confirm OTP with Firebase
        const cred = await otpState.firebaseConfirmation.confirm(otp);
        const idToken = await cred.user.getIdToken();

        // Exchange Firebase ID token for MGX JWT
        const response = await authService.firebasePhoneLogin({ idToken });

        // On success, immediately hydrate auth context + tokens so UI updates without refresh
        if (response && response.accessToken && response.user) {
          login(
            response.user,
            response.user.role || 'user',
            response.accessToken,
            response.refreshToken || null,
          );
        }

        setOtpState(prev => ({
          ...prev,
          otpVerified: true,
        }));

        return {
          success: !!response?.success,
          user: response.user || null,
          accessToken: response.accessToken || null,
        };
      }

      // Email or other OTP verification via backend
      let otpData = { otp };

      if (otpState.type === 'email' && otpState.email) {
        otpData.email = otpState.email;
      } else if (otpState.phoneNumber) {
        otpData.phone = otpState.phoneNumber;
      }

      const response = await authService.verifyOTP(otpData);

      // Update state on success
      setOtpState(prev => ({
        ...prev,
        otpVerified: true,
      }));

      // Store auth token if provided
      if (response.accessToken) {
        localStorage.setItem('auth_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        if (response.user) {
          login(response.user, response.user.role || 'user', response.accessToken, response.refreshToken);
        }
      }

      return { success: true, user: response.user || null, accessToken: response.accessToken || null };
    } catch (error) {
      throw new Error(error.message || 'Invalid OTP. Please try again.');
    }
  };

  // Email OTP Functions
  const sendEmailOTP = async (email) => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    // Clear any existing timer
    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
    }

    try {
      // Call real API to send OTP
      const response = await authService.sendOTP({ email, type: 'email' });

      // Update state on success
      setOtpState(prev => ({
        ...prev,
        email,
        otpSent: true,
        countdown: 60, // 60 seconds countdown
        canResend: false,
        type: 'email'
      }));

      // Start countdown timer
      otpTimerRef.current = setInterval(() => {
        setOtpState(prev => {
          if (prev.countdown <= 1) {
            clearInterval(otpTimerRef.current);
            otpTimerRef.current = null;
            return { ...prev, countdown: 0, canResend: true };
          }
          return { ...prev, countdown: prev.countdown - 1 };
        });
      }, 1000);

      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  };

  const verifyEmailOTP = async (otp) => {
    if (!otpState.otpSent) {
      throw new Error('Please request OTP first');
    }

    try {
      // Prepare data for OTP verification
      let otpData = { otp, email: otpState.email };

      // Call real API to verify OTP
      const response = await authService.verifyOTP(otpData);

      // Update state on success
      setOtpState(prev => ({
        ...prev,
        otpVerified: true
      }));

      // Store auth token if provided
      // Updated to handle new token structure
      if (response.accessToken) {
        localStorage.setItem('auth_token', response.accessToken);
        // Store refresh token if provided
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        // Also update user context with token
        if (response.user) {
          // Updated to use correct token names
          login(response.user, response.user.role || 'user', response.accessToken, response.refreshToken);
        }
      }

      return { success: true, user: response.user || null, accessToken: response.accessToken || null };
    } catch (error) {
      throw new Error(error.message || 'Invalid OTP. Please try again.');
    }
  };

  const resendOTP = async () => {
    if (otpState.type === 'phone' && otpState.phoneNumber) {
      return sendOTP(otpState.phoneNumber);
    } else if (otpState.type === 'email' && otpState.email) {
      return sendEmailOTP(otpState.email);
    } else {
      throw new Error('No contact information available for resend');
    }
  };

  const sendDefaultOTPAdmin = async (email) => {
    // Clear any existing timer
    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
    }

    try {
      const response = await authService.sendDefaultOTPAdmin(email);

      // Update state on success to allow verification
      setOtpState(prev => ({
        ...prev,
        email,
        otpSent: true,
        countdown: 60,
        canResend: false,
        type: 'email'
      }));

      // Start countdown timer
      otpTimerRef.current = setInterval(() => {
        setOtpState(prev => {
          if (prev.countdown <= 1) {
            clearInterval(otpTimerRef.current);
            otpTimerRef.current = null;
            return { ...prev, countdown: 0, canResend: true };
          }
          return { ...prev, countdown: prev.countdown - 1 };
        });
      }, 1000);

      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to send admin OTP');
    }
  };

  const resetOTP = () => {
    setOtpState({
      phoneNumber: '',
      email: '',
      otpSent: false,
      otpVerified: false,
      countdown: 0,
      canResend: false,
      type: 'phone',
      firebaseConfirmation: null,
    });

    if (otpTimerRef.current) {
      clearInterval(otpTimerRef.current);
      otpTimerRef.current = null;
    }
  };

  const value = {
    user,
    userType,
    login,
    logout,
    refreshToken,
    loading,
    otpState,
    sendOTP,
    verifyOTP,
    resendOTP,
    resetOTP,
    sendEmailOTP,
    verifyEmailOTP,
    sendDefaultOTPAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the AuthContext along with the provider
export { AuthContext };
export default AuthProvider;